import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, 
  Shield, 
  Crown, 
  Star,
  Medal,
  Users,
  TrendingUp,
  Sparkles,
  ChevronRight,
  Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Tier badge configurations
const TIER_BADGES = {
  bronze: { 
    icon: Shield, 
    color: 'text-amber-700', 
    bgColor: 'bg-amber-100',
    borderColor: 'border-amber-300',
    gradientFrom: 'from-amber-600',
    gradientTo: 'to-amber-800'
  },
  silver: { 
    icon: Medal, 
    color: 'text-slate-500', 
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    gradientFrom: 'from-slate-400',
    gradientTo: 'to-slate-600'
  },
  gold: { 
    icon: Award, 
    color: 'text-yellow-600', 
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-400',
    gradientFrom: 'from-yellow-400',
    gradientTo: 'to-amber-500'
  },
  platinum: { 
    icon: Crown, 
    color: 'text-slate-600', 
    bgColor: 'bg-gradient-to-r from-slate-100 to-purple-100',
    borderColor: 'border-purple-300',
    gradientFrom: 'from-slate-300',
    gradientTo: 'to-purple-400'
  }
};

// Rank badge component
const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <motion.div 
        className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg"
        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Crown className="w-5 h-5 text-white" />
      </motion.div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-10 h-10 bg-gradient-to-br from-slate-300 to-slate-500 rounded-full flex items-center justify-center shadow-md">
        <span className="text-white font-bold">2</span>
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-10 h-10 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center shadow-md">
        <span className="text-white font-bold">3</span>
      </div>
    );
  }
  return (
    <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
      <span className="text-stone-600 font-semibold">{rank}</span>
    </div>
  );
};

// Guardian row component
const GuardianRow = ({ guardian, isTopGuardian = false, showClubCount = false }) => {
  const tierConfig = TIER_BADGES[guardian.tier?.tier_id] || TIER_BADGES.bronze;
  const TierIcon = tierConfig.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`p-4 rounded-xl transition-all ${
        isTopGuardian 
          ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-300 shadow-md' 
          : 'bg-white hover:bg-stone-50 border border-stone-200'
      }`}
    >
      <div className="flex items-center gap-4">
        {/* Rank */}
        <RankBadge rank={guardian.rank} />
        
        {/* Avatar */}
        <div className="relative">
          <Avatar className="w-12 h-12 border-2 border-white shadow-md">
            <AvatarImage src={guardian.picture} />
            <AvatarFallback className={`bg-gradient-to-br ${tierConfig.gradientFrom} ${tierConfig.gradientTo} text-white font-bold`}>
              {guardian.name?.charAt(0) || 'G'}
            </AvatarFallback>
          </Avatar>
          {isTopGuardian && (
            <motion.div 
              className="absolute -top-1 -right-1"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Sparkles className="w-4 h-4 text-yellow-500" />
            </motion.div>
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-stone-900 truncate">
              {guardian.name || 'Anonymous Guardian'}
            </p>
            <Badge className={`${tierConfig.bgColor} ${tierConfig.color} border ${tierConfig.borderColor} text-xs`}>
              <TierIcon className="w-3 h-3 mr-1" />
              {guardian.tier?.name?.split(' ')[0]}
            </Badge>
          </div>
          <div className="flex items-center gap-3 text-sm text-stone-500 mt-1">
            <span className="flex items-center">
              <Shield className="w-3 h-3 mr-1" />
              ${(guardian.deposit_amount || guardian.total_deposits || 0).toFixed(2)}
            </span>
            {showClubCount && guardian.clubs_protected && (
              <span className="flex items-center">
                <Users className="w-3 h-3 mr-1" />
                {guardian.clubs_protected} clubs
              </span>
            )}
            <span className="flex items-center text-green-600">
              <TrendingUp className="w-3 h-3 mr-1" />
              +${guardian.monthly_earnings}/mo
            </span>
          </div>
        </div>
        
        {/* Protection Amount */}
        <div className="text-right">
          <p className="text-lg font-bold text-stone-900">
            ${(guardian.deposit_amount || guardian.total_deposits || 0).toFixed(2)}
          </p>
          <p className="text-xs text-stone-400">protected</p>
        </div>
      </div>
    </motion.div>
  );
};

// Club Guardian Leaderboard
export function ClubGuardianLeaderboard({ clubId, clubName }) {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (clubId) {
      fetchLeaderboard();
    }
  }, [clubId]);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/guardian-leaderboard/${clubId}?limit=10`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-48 bg-stone-100 rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  if (!leaderboard || leaderboard.total_guardians === 0) {
    const scrollToProtectionDeposit = () => {
      // Find protection deposit section using valid CSS selectors
      const protectionSection = document.querySelector('[data-testid="protection-deposit-card"]');
      if (protectionSection) {
        protectionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        // Scroll to top of page where protection deposit usually is
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    return (
      <Card className="overflow-hidden" data-testid="club-guardian-leaderboard-empty">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8" />
            <div>
              <h3 className="font-bold text-lg">Guardian Leaderboard</h3>
              <p className="text-white/80 text-sm">Be the first to protect this club!</p>
            </div>
          </div>
        </div>
        <CardContent className="p-6 text-center">
          <Shield className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-600 mb-4">No guardians yet. Add a protection deposit to be the first!</p>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={scrollToProtectionDeposit}
            data-testid="become-guardian-button"
          >
            Become a Guardian
          </Button>
        </CardContent>
      </Card>
    );
  }

  const protectionProgress = leaderboard.protection_goal > 0 
    ? Math.min(100, (leaderboard.total_protected / leaderboard.protection_goal) * 100)
    : 0;

  return (
    <Card className="overflow-hidden" data-testid="club-guardian-leaderboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Trophy className="w-8 h-8" />
            </motion.div>
            <div>
              <h3 className="font-bold text-lg">Guardian Leaderboard</h3>
              <p className="text-white/80 text-sm">{clubName || 'Club'} Protectors</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{leaderboard.total_guardians}</p>
            <p className="text-xs text-white/70">Guardians</p>
          </div>
        </div>
        
        {/* Protection Progress */}
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-white/80">Club Protection</span>
            <span className="font-medium">${leaderboard.total_protected.toFixed(2)}</span>
          </div>
          <Progress value={protectionProgress} className="h-2 bg-white/20" />
        </div>
      </div>

      {/* Top Guardian Spotlight */}
      {leaderboard.top_guardian && (
        <div className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-b border-yellow-200">
          <div className="flex items-center gap-2 mb-3">
            <Crown className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-semibold text-yellow-800">Top Guardian</span>
          </div>
          <GuardianRow guardian={leaderboard.top_guardian} isTopGuardian={true} />
        </div>
      )}

      {/* Leaderboard */}
      <CardContent className="p-4">
        <div className="space-y-3">
          {leaderboard.leaderboard.slice(expanded ? 0 : 0, expanded ? 10 : 5).map((guardian, index) => (
            <GuardianRow 
              key={guardian.user_id} 
              guardian={guardian}
              isTopGuardian={index === 0}
            />
          ))}
        </div>
        
        {leaderboard.leaderboard.length > 5 && (
          <Button
            variant="ghost"
            className="w-full mt-4"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show Less' : `Show All ${leaderboard.total_guardians} Guardians`}
            <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Global Guardian Leaderboard
export function GlobalGuardianLeaderboard() {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/guardian-leaderboard?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (error) {
      console.error('Error fetching global leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-64 bg-stone-100 rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  if (!leaderboard || leaderboard.total_guardians === 0) {
    return (
      <Card className="overflow-hidden" data-testid="global-guardian-leaderboard-empty">
        <CardContent className="p-8 text-center">
          <Trophy className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-700 mb-2">No Guardians Yet</h3>
          <p className="text-stone-500">Be the first to protect your club members!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden" data-testid="global-guardian-leaderboard">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Trophy className="w-10 h-10" />
            </motion.div>
            <div>
              <h2 className="text-2xl font-bold">Global Guardian Leaderboard</h2>
              <p className="text-white/80">Top protectors across all clubs</p>
            </div>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <p className="text-3xl font-bold">{leaderboard.total_guardians}</p>
            <p className="text-xs text-white/70">Total Guardians</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <p className="text-3xl font-bold">${(leaderboard.total_protected / 1000).toFixed(1)}K</p>
            <p className="text-xs text-white/70">Total Protected</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
            <div className="flex justify-center gap-1">
              {Object.entries(leaderboard.tier_distribution).map(([tier, count]) => (
                count > 0 && (
                  <Badge 
                    key={tier} 
                    className="text-xs"
                    style={{ backgroundColor: TIER_BADGES[tier]?.borderColor?.replace('border-', '') || '#e5e7eb' }}
                  >
                    {count}
                  </Badge>
                )
              ))}
            </div>
            <p className="text-xs text-white/70 mt-1">By Tier</p>
          </div>
        </div>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.leaderboard.length >= 3 && (
        <div className="p-6 bg-gradient-to-b from-stone-50 to-white border-b">
          <div className="flex items-end justify-center gap-4">
            {/* 2nd Place */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <Avatar className="w-16 h-16 mx-auto border-4 border-slate-300 shadow-lg">
                <AvatarImage src={leaderboard.leaderboard[1]?.picture} />
                <AvatarFallback className="bg-gradient-to-br from-slate-400 to-slate-600 text-white text-xl">
                  {leaderboard.leaderboard[1]?.name?.charAt(0) || '2'}
                </AvatarFallback>
              </Avatar>
              <div className="mt-2 bg-slate-200 rounded-t-lg px-4 py-2 h-16 flex items-end justify-center">
                <span className="text-2xl font-bold text-slate-600">2</span>
              </div>
              <p className="text-sm font-medium mt-2 truncate max-w-20">{leaderboard.leaderboard[1]?.name?.split(' ')[0]}</p>
              <p className="text-xs text-stone-500">${leaderboard.leaderboard[1]?.total_deposits?.toFixed(0)}</p>
            </motion.div>

            {/* 1st Place */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <motion.div
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Crown className="w-8 h-8 text-yellow-500 mx-auto mb-1" />
              </motion.div>
              <Avatar className="w-20 h-20 mx-auto border-4 border-yellow-400 shadow-xl">
                <AvatarImage src={leaderboard.leaderboard[0]?.picture} />
                <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-2xl">
                  {leaderboard.leaderboard[0]?.name?.charAt(0) || '1'}
                </AvatarFallback>
              </Avatar>
              <div className="mt-2 bg-gradient-to-b from-yellow-300 to-amber-400 rounded-t-lg px-6 py-2 h-24 flex items-end justify-center">
                <span className="text-3xl font-bold text-amber-800">1</span>
              </div>
              <p className="text-sm font-bold mt-2 truncate max-w-24">{leaderboard.leaderboard[0]?.name?.split(' ')[0]}</p>
              <p className="text-xs text-green-600 font-medium">${leaderboard.leaderboard[0]?.total_deposits?.toFixed(0)}</p>
            </motion.div>

            {/* 3rd Place */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <Avatar className="w-14 h-14 mx-auto border-4 border-amber-600 shadow-lg">
                <AvatarImage src={leaderboard.leaderboard[2]?.picture} />
                <AvatarFallback className="bg-gradient-to-br from-amber-600 to-amber-800 text-white text-lg">
                  {leaderboard.leaderboard[2]?.name?.charAt(0) || '3'}
                </AvatarFallback>
              </Avatar>
              <div className="mt-2 bg-amber-200 rounded-t-lg px-4 py-2 h-12 flex items-end justify-center">
                <span className="text-xl font-bold text-amber-700">3</span>
              </div>
              <p className="text-sm font-medium mt-2 truncate max-w-20">{leaderboard.leaderboard[2]?.name?.split(' ')[0]}</p>
              <p className="text-xs text-stone-500">${leaderboard.leaderboard[2]?.total_deposits?.toFixed(0)}</p>
            </motion.div>
          </div>
        </div>
      )}

      {/* Full Leaderboard */}
      <CardContent className="p-4">
        <div className="space-y-3">
          {leaderboard.leaderboard.slice(3).map((guardian) => (
            <GuardianRow 
              key={guardian.user_id} 
              guardian={guardian}
              showClubCount={true}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default GlobalGuardianLeaderboard;
